/**
 * Demo API — deterministic fake data layer for the Paramount walkthrough.
 *
 * When the demo launches, this module patches `window.fetch` so any API
 * request that matches a known route pattern is answered from a fixture
 * file plus a tiny in-memory mutation store. Writes (clock-in, observations,
 * mark complete, messages) update the store so subsequent reads reflect
 * them — but nothing ever reaches the real backend.
 *
 * The store is re-initialised from the fixture file on every enable(), so
 * run #5 of the demo shows exactly the same customers, stops, and progress
 * numbers as run #1. No real customer data ever appears on screen, and the
 * demo can never produce a real clock-in, completion email, or audit row.
 */

let fixtures = null;
let store = null;
let originalFetch = null;
let patched = false;
let savedManualTruckId = null;
const DEMO_TRUCK_LS_KEY = 'crew.manualTruckId';

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

async function loadFixtures() {
  const res = await fetch('/demo-fixtures.json');
  if (!res.ok) throw new Error(`demo: fixtures fetch failed (${res.status})`);
  return res.json();
}

/**
 * Patch `current_driver_user_id` on the primary demo truck to match the
 * currently-signed-in user, so CrewToday's auto-assign logic picks our
 * demo truck for whichever admin launches the walkthrough.
 */
function patchUserOntoTruck(store) {
  try {
    const userRaw = localStorage.getItem('auth_user');
    if (!userRaw) return;
    const user = JSON.parse(userRaw);
    if (!user || !user.id) return;
    const primary = store.trucks.find((t) => t.id === store.demoTruckId);
    if (primary) primary.current_driver_user_id = user.id;
  } catch {
    /* ignore */
  }
}

// ── Route matcher ─────────────────────────────────────────────────────

const routes = [];

function register(method, pattern, handler) {
  routes.push({ method, pattern, handler });
}

function resolveRoute(method, pathAndQuery) {
  for (const r of routes) {
    if (r.method !== method) continue;
    const m = pathAndQuery.match(r.pattern);
    if (m) return { handler: r.handler, params: m.slice(1) };
  }
  return null;
}

function splitUrl(url) {
  // Strip scheme+host if present. The app's fetch helper produces either
  // absolute URLs (dev) or relative ones (prod). Both need to match.
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname + u.search;
  } catch {
    return url;
  }
}

// ── Response helpers ──────────────────────────────────────────────────

function jsonResponse(data, status = 200) {
  return new Response(JSON.stringify(data), {
    status,
    headers: { 'Content-Type': 'application/json' },
  });
}

function errorResponse(msg, status = 500) {
  return jsonResponse({ error: msg }, status);
}

// ── Handlers ──────────────────────────────────────────────────────────

register('GET', /^\/api\/routes\/trucks(\?|$)/, async () => {
  return jsonResponse({ trucks: store.trucks });
});

register('GET', /^\/api\/routes\/daily(\?|$)/, async ({ query }) => {
  const truckId = query.get('truck_id');
  if (!truckId) {
    return jsonResponse({ route_items: [] });
  }
  // Only the primary demo truck has scripted stops; others are empty
  // so the UI treats them as "no work for you on this truck today".
  if (parseInt(truckId, 10) !== store.demoTruckId) {
    return jsonResponse({ route_items: [] });
  }
  return jsonResponse({
    route_items: store.dailyRoute.route_items.map((it) => ({ ...it })),
  });
});

register('POST', /^\/api\/jobs\/ensure-for-job-site$/, async ({ body }) => {
  // Map job_site_id → active_job_id from our fixture.
  const siteId = body && body.job_site_id;
  const item = store.dailyRoute.route_items.find((it) => it.id === siteId);
  if (!item) return jsonResponse({ job: null });
  return jsonResponse({
    job: { id: item.active_job_id, job_type: item.job_type || 'summer' },
  });
});

register('POST', /^\/api\/jobs\/(\d+)\/clock-in$/, async ({ params }) => {
  const jobId = params[0];
  const job = store.jobs[jobId];
  const item = store.dailyRoute.route_items.find((it) => String(it.active_job_id) === jobId);
  if (!job || !item) return errorResponse('job not found', 404);

  const now = new Date().toISOString();
  const wasOnBreak = !!item.clock_in_time && !!item.clock_out_time;

  if (wasOnBreak) {
    // Resume: clear clock_out, keep original clock_in.
    item.clock_out_time = null;
    if (job) job.clock_out_time = null;
  } else {
    item.clock_in_time = now;
    if (job) job.clock_in_time = now;
  }

  return jsonResponse({ resumed: wasOnBreak });
});

register('POST', /^\/api\/jobs\/(\d+)\/clock-out$/, async ({ params }) => {
  const jobId = params[0];
  const job = store.jobs[jobId];
  const item = store.dailyRoute.route_items.find((it) => String(it.active_job_id) === jobId);
  if (!job || !item) return errorResponse('job not found', 404);

  const now = new Date().toISOString();
  item.clock_out_time = now;
  if (job) job.clock_out_time = now;

  return jsonResponse({ ok: true });
});

register('POST', /^\/api\/jobs\/(\d+)\/complete-job$/, async ({ params, body }) => {
  const jobId = params[0];
  const job = store.jobs[jobId];
  const item = store.dailyRoute.route_items.find((it) => String(it.active_job_id) === jobId);
  if (!job || !item) return errorResponse('job not found', 404);

  const now = new Date().toISOString();
  item.completion_date = now;
  if (!item.clock_out_time) item.clock_out_time = now;
  if (job) {
    job.completion_date = now;
    if (!job.clock_out_time) job.clock_out_time = now;
    if (body && body.notes) job.completion_notes = body.notes;
  }

  return jsonResponse({ ok: true });
});

register('POST', /^\/api\/jobs\/(\d+)\/messages$/, async ({ params, body }) => {
  const jobId = params[0];
  const job = store.jobs[jobId];
  if (!job) return errorResponse('job not found', 404);
  const msg = {
    id: Math.floor(Math.random() * 1_000_000),
    note: (body && body.note) || '',
    created_at: new Date().toISOString(),
  };
  job.messages = job.messages || [];
  job.messages.push(msg);
  return jsonResponse({ message: msg });
});

register('GET', /^\/api\/jobs\/observation-templates(\?|$)/, async ({ query }) => {
  const type = query.get('observation_type') || 'summer';
  const templates =
    (store.observationTemplates && store.observationTemplates[type]) || [];
  return jsonResponse({ templates });
});

register('POST', /^\/api\/jobs\/(\d+)\/observations$/, async ({ params, body }) => {
  const jobId = params[0];
  const job = store.jobs[jobId];
  if (!job) return errorResponse('job not found', 404);
  const tplId = body && body.observation_template_id;
  const templatesByType =
    (store.observationTemplates && store.observationTemplates[job.job_type || 'summer']) || [];
  const template = tplId != null ? templatesByType.find((t) => t.id === tplId) : null;

  const observation = {
    id: Math.floor(Math.random() * 1_000_000),
    template_name: template ? template.name : null,
    title: template ? template.title : null,
    custom_note: (body && body.custom_note) || null,
    severity: (body && body.severity) || 'normal',
    customer_visible: !!(body && body.customer_visible),
    noted_at: new Date().toISOString(),
  };
  job.observations = job.observations || [];
  job.observations.push(observation);
  return jsonResponse({ observation });
});

register('GET', /^\/api\/jobs\/(\d+)(\?|$)/, async ({ params }) => {
  const jobId = params[0];
  const job = store.jobs[jobId];
  if (!job) return errorResponse('job not found', 404);
  return jsonResponse({ job });
});

// ── Operations dashboard endpoints ────────────────────────────────────

register('GET', /^\/api\/jobs\/stats\/dashboard(\?|$)/, async () => {
  return jsonResponse(store.dashboardStats);
});

register('GET', /^\/api\/routes\/job-sites(\?|$)/, async () => {
  return jsonResponse({ job_sites: store.jobSites });
});

register('GET', /^\/api\/routes\/today-active-counts(\?|$)/, async () => {
  return jsonResponse({ counts: store.todayActiveCounts });
});

register('GET', /^\/api\/gps\/locations\/current(\?|$)/, async () => {
  const updated = new Date().toISOString();
  return jsonResponse({
    truck_locations: store.gpsLocations.map((g) => ({ ...g, updated_at: updated })),
  });
});

// ── Summer / Winter detail PATCH calls are tolerated ──────────────────
// These come from the SummerJobDetails / WinterJobDetails inner components
// when a crew checks a service box. The demo doesn't script those clicks
// but the components may fire them on open for default state. Accept any
// PATCH to /api/jobs/:id and silently apply the body to our job.

register('PATCH', /^\/api\/jobs\/(\d+)(\?|$)/, async ({ params, body }) => {
  const jobId = params[0];
  const job = store.jobs[jobId];
  if (!job) return errorResponse('job not found', 404);
  if (body && typeof body === 'object') {
    Object.assign(job, body);
  }
  return jsonResponse({ job });
});

// ── Fetch wrapper ─────────────────────────────────────────────────────

async function demoFetch(input, init = {}) {
  let url;
  if (typeof input === 'string') {
    url = input;
  } else if (input && typeof input.url === 'string') {
    url = input.url;
  } else {
    return originalFetch(input, init);
  }

  const pathAndQuery = splitUrl(url);
  const method = (init.method || 'GET').toUpperCase();

  const match = resolveRoute(method, pathAndQuery);
  if (!match) {
    return originalFetch(input, init);
  }

  let body = null;
  if (init.body) {
    if (typeof init.body === 'string') {
      try { body = JSON.parse(init.body); } catch { body = init.body; }
    } else {
      body = init.body;
    }
  }

  let query;
  try {
    const qIdx = pathAndQuery.indexOf('?');
    query = new URLSearchParams(qIdx >= 0 ? pathAndQuery.slice(qIdx + 1) : '');
  } catch {
    query = new URLSearchParams();
  }

  try {
    return await match.handler({
      params: match.params,
      body,
      query,
      url: pathAndQuery,
      method,
    });
  } catch (err) {
    console.error('demoApi handler error', err);
    return errorResponse(err.message || 'demo handler crashed', 500);
  }
}

// ── Public API ────────────────────────────────────────────────────────

export async function enableDemoApi() {
  if (patched) return;
  if (!fixtures) fixtures = await loadFixtures();

  // Fresh store for every launch — wipes any mutations from a previous run.
  store = deepClone(fixtures);
  patchUserOntoTruck(store);

  // Pin the manual-truck pick so the crew page snaps to our demo truck
  // even if the admin happens to be assigned as driver elsewhere.
  savedManualTruckId = localStorage.getItem(DEMO_TRUCK_LS_KEY);
  try {
    localStorage.setItem(DEMO_TRUCK_LS_KEY, String(store.demoTruckId));
  } catch { /* ignore */ }

  originalFetch = window.fetch.bind(window);
  window.fetch = demoFetch;
  patched = true;
  console.log('demoApi: enabled (fixtures loaded, fetch patched)');
}

export function disableDemoApi() {
  if (!patched) return;
  window.fetch = originalFetch;
  originalFetch = null;

  // Restore whatever the user had in localStorage before the demo started,
  // so post-demo the Crew page behaves normally.
  try {
    if (savedManualTruckId == null) {
      localStorage.removeItem(DEMO_TRUCK_LS_KEY);
    } else {
      localStorage.setItem(DEMO_TRUCK_LS_KEY, savedManualTruckId);
    }
  } catch { /* ignore */ }
  savedManualTruckId = null;

  store = null;
  patched = false;
  console.log('demoApi: disabled (fetch restored)');
}

export function isDemoApiActive() {
  return patched;
}
